import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Search, User, Users, Check, ChevronRight, 
  AlertCircle, MessageCircle, Plus
} from 'lucide-react';
import { conversationsAPI, permissionsAPI } from '../../services/messagingAPI';

import { API_URL } from '../../config/api';

const NewConversationModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState('type'); // 'type', 'search', 'group'
  const [conversationType, setConversationType] = useState('direct');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  // Recherche d'utilisateurs
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('ecosystem_token');
        const response = await fetch(`${API_URL}/api/ecosystem/users/search?q=${encodeURIComponent(searchQuery)}&limit=20`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.users || []);
        }
      } catch (error) {
        console.error('Erreur recherche:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelectUser = async (user) => {
    setError('');
    
    if (conversationType === 'direct') {
      // Vérifier les permissions
      try {
        const canMsg = await permissionsAPI.canMessage(user.id);
        if (!canMsg.can_message) {
          if (canMsg.reason === 'not_mutual_follow') {
            setError('Vous devez vous suivre mutuellement pour discuter avec cet utilisateur.');
          } else {
            setError('Impossible d\'envoyer un message à cet utilisateur.');
          }
          return;
        }
        
        // Créer la conversation directe
        await createConversation([user.id]);
      } catch (error) {
        setError(error.message);
      }
    } else {
      // Groupe: ajouter à la sélection
      if (selectedUsers.find(u => u.id === user.id)) {
        setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
      } else {
        setSelectedUsers(prev => [...prev, user]);
      }
    }
  };

  const createConversation = async (participantIds) => {
    try {
      setCreating(true);
      const data = {
        type: conversationType,
        participant_ids: participantIds,
        ...(conversationType === 'group' && { group_name: groupName })
      };
      
      const conversation = await conversationsAPI.create(data);
      onCreated(conversation);
    } catch (error) {
      setError(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateGroup = () => {
    if (!groupName.trim()) {
      setError('Le nom du groupe est requis');
      return;
    }
    if (selectedUsers.length === 0) {
      setError('Ajoutez au moins un participant');
      return;
    }
    createConversation(selectedUsers.map(u => u.id));
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10002] flex items-end md:items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full md:w-[480px] md:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'type' && 'Nouvelle conversation'}
            {step === 'search' && (conversationType === 'direct' ? 'Choisir un contact' : 'Ajouter des participants')}
            {step === 'group' && 'Créer un groupe'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Type selection */}
            {step === 'type' && (
              <motion.div
                key="type"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-4 space-y-3"
              >
                <button
                  onClick={() => {
                    setConversationType('direct');
                    setStep('search');
                  }}
                  className="w-full p-4 bg-gray-50 hover:bg-orange-50 rounded-xl flex items-center gap-4 transition-colors group"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                    <User className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">Message privé</p>
                    <p className="text-sm text-gray-500">Discuter avec un utilisateur</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                <button
                  onClick={() => {
                    setConversationType('group');
                    setStep('search');
                  }}
                  className="w-full p-4 bg-gray-50 hover:bg-blue-50 rounded-xl flex items-center gap-4 transition-colors group"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">Créer un groupe</p>
                    <p className="text-sm text-gray-500">Discuter avec plusieurs personnes</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </motion.div>
            )}

            {/* Step 2: User search */}
            {step === 'search' && (
              <motion.div
                key="search"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex flex-col h-full"
              >
                {/* Selected users (group) */}
                {conversationType === 'group' && selectedUsers.length > 0 && (
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map(user => (
                        <div 
                          key={user.id}
                          className="flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm"
                        >
                          <span>{user.display_name || user.company_name}</span>
                          <button 
                            onClick={() => setSelectedUsers(prev => prev.filter(u => u.id !== user.id))}
                            className="hover:text-orange-900"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search input */}
                <div className="p-4 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un utilisateur..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Search results */}
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : searchQuery.length < 2 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Tapez au moins 2 caractères pour rechercher</p>
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Aucun utilisateur trouvé</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {searchResults.map(user => {
                        const isSelected = selectedUsers.find(u => u.id === user.id);
                        return (
                          <button
                            key={user.id}
                            onClick={() => handleSelectUser(user)}
                            disabled={creating}
                            className={`w-full p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                              isSelected ? 'bg-orange-50' : ''
                            }`}
                          >
                            {/* Avatar */}
                            {user.avatar_url ? (
                              <img 
                                src={user.avatar_url} 
                                alt="" 
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                                <User className="w-6 h-6 text-gray-500" />
                              </div>
                            )}

                            {/* Info */}
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  {user.display_name || user.company_name || 'Utilisateur'}
                                </span>
                                {user.user_type === 'professionnel' && (
                                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                    PRO
                                  </span>
                                )}
                              </div>
                              {user.business_sector && (
                                <p className="text-sm text-gray-500">{user.business_sector}</p>
                              )}
                            </div>

                            {/* Selection indicator */}
                            {conversationType === 'group' && (
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                              }`}>
                                {isSelected && <Check className="w-4 h-4 text-white" />}
                              </div>
                            )}

                            {conversationType === 'direct' && (
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Group: Next button */}
                {conversationType === 'group' && selectedUsers.length > 0 && (
                  <div className="p-4 border-t border-gray-200">
                    <button
                      onClick={() => setStep('group')}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors"
                    >
                      Continuer ({selectedUsers.length} sélectionné{selectedUsers.length > 1 ? 's' : ''})
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Group name */}
            {step === 'group' && (
              <motion.div
                key="group"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-4"
              >
                {/* Group avatar preview */}
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <Users className="w-12 h-12 text-white" />
                  </div>
                </div>

                {/* Group name input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du groupe
                  </label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Ex: Équipe chantier Paris"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    autoFocus
                  />
                </div>

                {/* Participants */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Participants ({selectedUsers.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.map(user => (
                      <div 
                        key={user.id}
                        className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full text-sm"
                      >
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                        ) : (
                          <User className="w-4 h-4 text-gray-500" />
                        )}
                        <span>{user.display_name || user.company_name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Create button */}
                <button
                  onClick={handleCreateGroup}
                  disabled={creating || !groupName.trim()}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-5 h-5" />
                      Créer le groupe
                    </>
                  )}
                </button>

                {/* Back button */}
                <button
                  onClick={() => setStep('search')}
                  className="w-full mt-3 text-gray-600 hover:text-gray-900 py-2 text-sm"
                >
                  Modifier les participants
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default NewConversationModal;
